// Pure helpers for building PocketBase filter strings from community
// programme list URL search params. No React / route imports here.

import { isPeriod, type Period } from '@/lib/analytics/periods'

export type TrendKind = 'trends' | 'complexity'

/**
 * A ranked programme lookup: `trends` ranks by workouts done in the period,
 * `complexity` by ratings at `level` or harder in the period. Ids come back
 * in rank order.
 */
export type TrendQuery =
    | { kind: 'trends'; period: Period }
    | { kind: 'complexity'; period: Period; level: number }

export interface ProgrammeQueryResolvers {
    trendingIds(query: TrendQuery): Promise<string[]>
    bookmarkedIds(): Promise<string[]>
    followedIds(): Promise<string[]>
}

type FieldKind = 'eq' | 'contains' | 'range' | 'eqNumber'

// Every URL param key the community programmes UI is allowed to turn into a
// filter clause. Anything else is silently ignored.
const SUPPORTED_FIELDS: Record<string, FieldKind> = {
    age: 'eq',
    level: 'eq',
    goals: 'eq',
    language: 'eq',
    'sexAtBirth?~': 'contains',
    'language?~': 'contains',
    'tags?~': 'contains',
    'exercises?~': 'contains',
    'exercises.equipment?~': 'contains',
    'exercises.muscleGroup?~': 'contains',
    meanWorkoutDuration: 'range',
    numberOfWorkouts: 'range',
    programmeDuration: 'range',
    price: 'range'
}

const IGNORED_KEYS = new Set(['page', 'perPage', 'totalPages'])

/** Whether a search param is a filter rather than paging. */
export function isFilterParam(key: string): boolean {
    return !IGNORED_KEYS.has(key)
}

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 20
const MIN_PER_PAGE = 1
const MAX_PER_PAGE = 100

const CONTROL_CHAR_CODES = (() => {
    const codes: number[] = []
    for (let i = 0; i <= 0x1f; i++) codes.push(i)
    codes.push(0x7f)
    return codes
})()

function stripControlChars(value: string): string {
    let result = value
    for (const code of CONTROL_CHAR_CODES) {
        result = result.split(String.fromCharCode(code)).join('')
    }
    return result
}

// PocketBase string literal escaping: backslash and single quote must be
// escaped, and control characters (including newlines) are stripped since
// they cannot appear inside a filter literal.
export function escapeFilterLiteral(value: string): string {
    return stripControlChars(value)
        .split('\\')
        .join('\\\\')
        .split("'")
        .join("\\'")
}

export function parsePagination(searchParams: URLSearchParams): {
    page: number
    perPage: number
} {
    const rawPage = parseInt(searchParams.get('page') || '', 10)
    const page =
        Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : DEFAULT_PAGE

    const rawPerPage = parseInt(searchParams.get('perPage') || '', 10)
    const perPage = Number.isFinite(rawPerPage) ? rawPerPage : DEFAULT_PER_PAGE

    return {
        page,
        perPage: Math.min(MAX_PER_PAGE, Math.max(MIN_PER_PAGE, perPage))
    }
}

function renderIdGroup(field: 'origId' | 'author', ids: string[]): string {
    const cleaned = ids.filter((id) => id)
    if (cleaned.length === 0) return ''
    return `(${cleaned
        .map((id) => `${field}='${escapeFilterLiteral(id)}'`)
        .join('||')})`
}

function renderRange(key: string, value: string): string[] {
    const [rawMin, rawMax] = value.split(',')
    if (rawMin === undefined || rawMax === undefined) return []

    const min = Number(rawMin)
    const max = Number(rawMax)
    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return []

    return [`${key}>=${min}`, `${key}<=${max}`]
}

export async function buildProgrammeFilter(
    searchParams: URLSearchParams,
    resolvers: ProgrammeQueryResolvers
): Promise<{ filter?: string; forceEmpty: boolean; order?: string[] }> {
    // Id lookups run concurrently; each settles to its clause, to null when
    // it matched nothing (the whole result is empty), or to '' to drop it.
    const parts: (string | Promise<string | null>)[] = [`status='published'`]
    // The complexity filter ranks over its own `period`, else the trends
    // one, else a week. Its own lets a link ask for "rated hard today"
    // without also requiring workouts today.
    const period: Period =
        [searchParams.get('period'), searchParams.get('trends')].find(
            isPeriod
        ) ?? 'week'
    let order: Promise<string[] | undefined> | undefined
    const idGroup =
        (field: 'origId' | 'author') =>
        (ids: string[]): string | null =>
            renderIdGroup(field, ids) || null

    for (const [key, value] of Array.from(searchParams)) {
        if (!value || value === 'undefined' || IGNORED_KEYS.has(key)) {
            continue
        }

        if (key === 'trends' || key === 'complexity') {
            const query = trendQuery(key, value, period)
            if (!query) continue
            const ids = resolvers.trendingIds(query)
            // The first ranked lookup orders the list. A failed one drops
            // its clause below and leaves the list unranked.
            order ??= ids.catch(() => undefined)
            parts.push(
                ids.then(idGroup('origId'), (error) => {
                    console.error('error', error)
                    return ''
                })
            )
            continue
        }

        if (key === 'bookmarks' && value === 'bookmarked') {
            parts.push(resolvers.bookmarkedIds().then(idGroup('origId')))
            continue
        }

        if (key === 'followings' && value === 'following') {
            parts.push(resolvers.followedIds().then(idGroup('author')))
            continue
        }

        const kind = SUPPORTED_FIELDS[key]
        if (!kind) continue

        if (kind === 'contains') {
            parts.push(`${key} '${escapeFilterLiteral(value)}'`)
            continue
        }

        if (kind === 'range') {
            parts.push(...renderRange(key, value))
            continue
        }

        if (kind === 'eqNumber') {
            const num = Number(value)
            if (Number.isFinite(num)) {
                parts.push(`${key}=${num}`)
            }
            continue
        }

        parts.push(`${key}='${escapeFilterLiteral(value)}'`)
    }

    const [clauses, ranked] = await Promise.all([Promise.all(parts), order])
    return {
        filter: clauses.filter(Boolean).join('&&'),
        forceEmpty: clauses.includes(null),
        order: ranked
    }
}

/**
 * Whether the params rank the list (a trends or complexity filter): the
 * whole list is read once and shown a page at a time, instead of paged by
 * the backend.
 */
export function hasRankedFilter(searchParams: URLSearchParams): boolean {
    const period =
        [searchParams.get('period'), searchParams.get('trends')].find(
            isPeriod
        ) ?? 'week'
    return Array.from(searchParams).some(
        ([key, value]) =>
            (key === 'trends' || key === 'complexity') &&
            !!trendQuery(key, value, period)
    )
}

function trendQuery(
    key: TrendKind,
    value: string,
    period: Period
): TrendQuery | undefined {
    if (key === 'trends') {
        return isPeriod(value) ? { kind: 'trends', period: value } : undefined
    }
    const level = Number(value)
    return Number.isInteger(level) && level >= 1 && level <= 5
        ? { kind: 'complexity', period, level }
        : undefined
}
